import React, { useState, useEffect, useRef } from 'react';
import { Play, Wand2, X } from 'lucide-react';
import IconTabs, { tabs } from './hero/IconTabs';
import VideoBento from './hero/VideoBento';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from './AnimatedBorderBadge';
import FeaturesHeroBento from '@/components/features/FeaturesHeroBento';

const EditorialHero: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const intervalRef = useRef<number | null>(null);
  const { theme } = useTheme();

  // Auto-cycle through tabs
  useEffect(() => {
    const startInterval = () => {
      intervalRef.current = window.setInterval(() => {
        setVideoProgress((prev) => {
          if (prev >= 100) {
            setActiveTab((currentTab) => (currentTab + 1) % tabs.length);
            return 0;
          }
          return prev + 2;
        });
      }, 100);
    };

    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setVideoProgress(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = window.setInterval(() => {
      setVideoProgress((prev) => {
        if (prev >= 100) {
          setActiveTab((currentTab) => (currentTab + 1) % tabs.length);
          return 0;
        }
        return prev + 2;
      });
    }, 100);
  };

  const handleWatchDemo = () => {
    setShowVideoModal(true);
  };

  const handleCloseModal = () => {
    setShowVideoModal(false);
  };

  return (
    <section 
      className="min-h-screen pt-32 lg:pt-60 pb-8 transition-colors duration-300"
      style={{ 
        background: theme === 'dark' 
          ? '#111111' 
          : 'linear-gradient(to bottom, #FFFFFF 0%, #FCFCF9 100%)'
      }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-8 lg:px-8">
        {/* Centered Text Content */}
        <div className="flex flex-col items-center text-center space-y-6 max-w-3xl mx-auto mb-12">
          {/* Customer Relationship Magic Badge */}
          <AnimatedBorderBadge text="Customer Relationship Magic" icon={Wand2} />
          <h1 
            className="text-3xl lg:text-4xl font-bold leading-[1.1] tracking-tight transition-colors duration-300"
            style={{ color: theme === 'dark' ? '#FFFFFF' : '#111111' }}
          >
            <span className="sm:whitespace-nowrap">
              Manage & Grow <span className="block sm:inline">Your Entire Business.</span>
            </span>
            <br className="hidden sm:block" />
            <span className="block sm:inline">One Platform.</span>
            <span className="block sm:inline"> One Price.</span>
          </h1>

          <p 
            className="text-sm leading-relaxed max-w-lg lg:max-w-2xl transition-colors duration-300" 
            style={{ color: theme === 'dark' ? '#AAAAAA' : '#555555' }}
          >
            The AI-Powered CRM that automates your growth & kills your software bloat.
          </p>


          <div className="pt-2 flex items-center gap-3">
            <button 
              onClick={handleWatchDemo}
              className="w-[140px] px-4 py-2 bg-white text-black text-sm font-medium border border-[#CCCCCC] hover:shadow-[inset_0_0_0_1px_#000000] hover:scale-[1.02] active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 rounded-lg"
            >
              <Play className="w-3 h-3" fill="currentColor" />
              Watch Demo
            </button>
            <button 
              onClick={() => document.getElementById('pricing-section')?.scrollIntoView({ behavior: 'smooth' })}
              className="w-[140px] px-4 py-2 bg-black text-white text-sm font-medium hover:bg-[#222222] hover:scale-[1.02] hover:shadow-md active:scale-[0.98] transition-all duration-200 flex items-center justify-center gap-2 rounded-lg"
            >
              Start for free →
            </button>
          </div>
        </div>

        {/* Mobile-only: Compact FeaturesHeroBento animation */}
        <div className="block sm:hidden max-w-6xl mx-auto w-full overflow-visible -mx-4">
          <div className="h-[320px]">
            <FeaturesHeroBento />
          </div>
        </div>

        {/* Tablet/Desktop: Original Interactive Video Demo */}
        <div className="hidden sm:block max-w-6xl mx-auto w-full">
          <div className="relative">
            <div 
              className="p-4 transition-colors duration-300"
              style={{ 
                backgroundColor: theme === 'dark' ? '#1A1A1A' : '#FFFFFF',
                border: `1px solid ${theme === 'dark' ? '#333333' : '#E0E0E0'}`, 
                borderRadius: '16px',
              }}
            >
              <IconTabs 
                activeTab={activeTab} 
                videoProgress={videoProgress} 
                onTabClick={handleTabClick} 
              />
              
              {/* Horizontal divider line */}
              <div 
                className="h-px w-full mb-4"
                style={{ 
                  backgroundColor: theme === 'dark' ? '#333333' : '#E0E0E0'
                }}
              />
              
              <VideoBento activeTab={activeTab} />
            </div>
            
            {/* White fade overlay - positioned inside border radius */}
            <div 
              className="absolute bottom-[17px] left-[17px] right-[17px] h-24 pointer-events-none"
              style={{
                background: theme === 'dark'
                  ? 'linear-gradient(to top, rgba(26,26,26,1) 0%, rgba(26,26,26,0) 100%)'
                  : 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
                borderBottomLeftRadius: '12px',
                borderBottomRightRadius: '12px',
              }}
            />
          </div>
          
          {/* Integration badge - below bento box */}
          <div className="flex justify-center mt-12 -mb-4">
            <AnimatedBorderBadge text="Integrate all your favorite apps" />
          </div>
        </div>
      </div>

      {/* Video Modal */}
      {showVideoModal && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm"
          onClick={handleCloseModal}
        >
          <div 
            className="relative w-full max-w-4xl mx-4 bg-black rounded-2xl overflow-hidden shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-10 p-2 bg-black/50 hover:bg-black/70 rounded-full text-white transition-colors"
            >
              <X className="w-5 h-5" />
            </button>
            
            {/* Video */}
            <video
              autoPlay
              controls
              className="w-full aspect-video"
              src="https://kpaixcycljzsxxriyzjh.supabase.co/storage/v1/object/public/video-content/Explainer%20V6%20-%20Complete%20Overview%20Explainer.mp4"
            />
          </div>
        </div>
      )}
    </section>
  );
};

export default EditorialHero;
